import React, { useState } from "react";
import Add from "./Add";
import DynamicEditComponent from "./Edit";
import { Print } from "./Print";
import { Search } from "./Search";
import Delete from "./Delete";

const ActionBar = ({
  title,
  fields,
  apiEndpoint,
  data,
  selectedRow,
  onRowSelect,
  onRefresh,
  onSearch,
  userRole, // New prop to check user role
  rowKey = "id",
  deleteMessage = "Are you sure you want to delete this item?",
}) => {
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedDeleteId, setSelectedDeleteId] = useState(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  // Check if user has admin privileges
  const isAdmin = userRole === "admin"; // Adjust based on your role naming convention

  const handleAdd = () => setShowAddModal(true);
  const handleCloseAdd = () => setShowAddModal(false);

  const handleEdit = () => {
    if (!selectedRow) {
      alert("Please select a row to edit!");
      return;
    }
    setShowEditModal(true);
  };

  const handleCloseEdit = () => {
    setShowEditModal(false);
    onRowSelect(null);
  };

  const handleDeleteConfirmation = (id) => {
    setSelectedDeleteId(id);
    setShowDeleteModal(true);
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setSelectedDeleteId(null);
  };

  const renderModal = (Component, props, handleClose) => (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
      <div className="bg-white p-6 rounded shadow-md w-full max-w-md mx-4">
        <Component {...props} onClose={handleClose} />
      </div>
    </div>
  );

  return (
    <div className="flex flex-col gap-4">
      {/* Desktop View */}
      <div className="hidden md:flex items-center gap-4 p-4 bg-gray-100 shadow-md">
        {isAdmin && (
          <>
            <button
              onClick={handleAdd}
              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
            >
              Add
            </button>
            <button
              onClick={handleEdit}
              className="px-4 py-2 bg-yellow-500 text-white rounded hover:bg-yellow-600"
            >
              Edit
            </button>
            <button
              onClick={() => {
                if (!selectedRow) {
                  alert("Please select a row to delete!");
                  return;
                }
                handleDeleteConfirmation(selectedRow[rowKey]);
              }}
              className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
            >
              Delete
            </button>
          </>
        )}
        <Print data={data} />
        <Search onSearch={onSearch} />
      </div>

      {/* Mobile View */}
      <div className="md:hidden flex flex-col justify-between items-center p-4 bg-gray-100 shadow-md">
        <h2 className="text-lg font-semibold">{title}</h2>
        <button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="px-3 py-2 bg-blue-500 text-white rounded"
        >
          {isMobileMenuOpen ? "Close" : "Actions"}
        </button>
      </div>

      {/* Mobile Action Menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden flex flex-col gap-4 p-4 bg-gray-200">
          {isAdmin && (
            <>
              <button
                onClick={() => {
                  handleAdd();
                  setIsMobileMenuOpen(false);
                }}
                className="w-full px-4 py-3 bg-blue-500 text-white rounded hover:bg-blue-600"
              >
                Add
              </button>
              <button
                onClick={() => {
                  handleEdit();
                  setIsMobileMenuOpen(false);
                }}
                className="w-full px-4 py-3 bg-yellow-500 text-white rounded hover:bg-yellow-600"
              >
                Edit
              </button>
              <button
                onClick={() => {
                  if (!selectedRow) {
                    alert("Please select a row to delete!");
                    return;
                  }
                  handleDeleteConfirmation(selectedRow[rowKey]);
                  setIsMobileMenuOpen(false);
                }}
                className="w-full px-4 py-3 bg-red-500 text-white rounded hover:bg-red-600"
              >
                Delete
              </button>
            </>
          )}
          <div className="flex gap-4">
            <Print data={data} className="flex-1" />
            <Search onSearch={onSearch} className="flex-1" />
          </div>
        </div>
      )}

      {/* Modals remain the same */}
      {showAddModal &&
        renderModal(
          Add,
          {
            title: `Add ${title}`,
            fields,
            apiEndpoint,
            onSuccess: onRefresh,
            onClose: handleCloseAdd,
          },
          handleCloseAdd
        )}

      {showEditModal &&
        selectedRow &&
        renderModal(
          DynamicEditComponent,
          {
            fields,
            data: selectedRow,
            apiEndpoint,
            rowKey,
            onSave: () => {
              onRefresh();
              handleCloseEdit();
              onRowSelect(null);
            },
          },
          handleCloseEdit
        )}

      {showDeleteModal &&
        renderModal(
          () => (
            <>
              <h2 className="text-xl font-semibold">Confirm Deletion</h2>
              <p className="my-4">{deleteMessage}</p>
              <div className="flex justify-end gap-4">
                <Delete
                  itemId={selectedDeleteId}
                  apiEndpoint={apiEndpoint}
                  rowKey={rowKey}
                  onDelete={() => {
                    onRefresh();
                    handleCancelDelete();
                  }}
                />
                <button
                  onClick={handleCancelDelete}
                  className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
                >
                  Cancel
                </button>
              </div>
            </>
          ),
          {},
          handleCancelDelete
        )}
    </div>
  );
};

export default ActionBar;
